import { type ConsoleUser, type Organization, type PlatformUser, findUserOrganization } from '@/stores/auth'
import type { BillingCycle, Period, SubscriptionState, SubscriptionStatus } from '@/types/subscription'

/**
 * Convert API period format to internal billing cycle format
 */
export function periodToBillingCycle(period: Period): BillingCycle {
	return period === 'Yearly' ? 'annual' : 'monthly'
}

/**
 * Convert API subscription state to internal subscription status
 */
export function subscriptionStateToStatus(state: SubscriptionState): SubscriptionStatus {
	switch (state) {
		case 'active':
			return 'active'
		case 'pending_initial_payment':
			return 'payment_failed'
		case 'cancelled_manually':
			return 'cancelled'
		case 'cancelled':
			return 'expired'
		default:
			return 'active'
	}
}

/**
 * Resolves the active organization from user context using direct ID match, tenant fallback, and array index fallback.
 */
export function resolveActiveOrganization(
	user: ConsoleUser | PlatformUser | null | undefined,
	currentOrgId: string | null | undefined,
	currentTenantId?: string | null,
): Organization | null {
	if (!user) {
		return null
	}

	// 1. Try to find the exact organization matching currentOrgId
	const exactMatch = findUserOrganization(user, currentOrgId)
	if (exactMatch) {
		return exactMatch
	}

	// 2. If not found, fall back to the active tenant's organization
	if ('tenant_list' in user && user.tenant_list) {
		const fallbackTenant = user.tenant_list.find(t => t.id === currentTenantId) ?? user.tenant_list[0]
		if (fallbackTenant?.organization) {
			return fallbackTenant.organization
		}
	}

	// 3. If still not found, fall back to the first organization in user.organizations
	if ('organizations' in user && user.organizations && user.organizations.length > 0) {
		return user.organizations[0]
	}

	return null
}
